import React from 'react';
import {diApi} from '@di-shared/api/di';
import BottomSheetWebViewWrapper from '@di-shared/features/common/BottomSheetWebViewWrapper';
import ImageSelector from '@di-shared/features/common/ImageSelector';
import useApiError from '@di-shared/features/common/useApiError';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import {splitTireName} from '@di-shared/features/diag/tire/tirePressureUtil';
import useTirePressure from '@di-shared/features/diag/tire/useTirePressure';
import environmentStore from '@di-shared/features/environment/environmentStore';
import PreventForm from '@shared/PreventForm';
import usePendingHandler from '@shared/usePendingHandler';

interface TireSelectBottomSheetProps {
  vin: string;
}

function TireSelectBottomSheetKR({vin}: TireSelectBottomSheetProps) {
  const {t} = useInferableTranslation();
  const {resolveError} = useApiError();
  const puid = environmentStore.getState().puid;

  const {recommendedTires, changeSelectedTire, selectedTire, registeredTire} = useTirePressure(vin);
  const handleRadioChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    changeSelectedTire(e.target.value);
  };

  const [handleSubmitTireRegistration] = usePendingHandler(
    async () => {
      await diApi.tire.createTire({
        requestBody: {
          puid,
          vin,
          tire: selectedTire,
        },
      });
      //FIXME: 사실 tire bound api임. 이름이 잘못됐음
      window.bottomSheetClose(true);
    },
    async (error) => {
      await resolveError(error);
      window.bottomSheetClose(false);
    }
  );

  return (
    <BottomSheetWebViewWrapper bottomSheetFallbackHeight={300}>
      <div className="bottom_sheet">
        <div className="bottom_sheet_header">
          <h2 className="bottom_sheet_title">{t('T30014')}</h2>
          <button
            type="button"
            className="btn btn_ic"
            aria-label={t('T10017')}
            onClick={() => {
              window.bottomSheetClose(null);
            }}
          >
            <span
              className="ic ic_close_20px"
              aria-hidden="true"
            ></span>
          </button>
        </div>
        <PreventForm onSubmit={handleSubmitTireRegistration}>
          <div className="bottom_sheet_inner">
            <div className="bottom_sheet_contents">
              <div className="tire_intro">
                <ImageSelector
                  className="tire_intro_img"
                  alt="tire_intro_img"
                  kiaLightSrc={() => import('@di-shared/assets/img/car_management/tire_select_intro_light.png')}
                  kiaDarkSrc={() => import('@di-shared/assets/img/car_management/tire_select_intro.png')}
                  hyundaiLightSrc={() => import('@di-shared/assets/img/car_management/hyundai/tire_select_intro_light.png')}
                  hyundaiDarkSrc={() => import('@di-shared/assets/img/car_management/hyundai/tire_select_intro.png')}
                  genesisLightSrc={() => import('@di-shared/assets/img/car_management/genesis/tire_select_intro_light.png')}
                  genesisDarkSrc={() => import('@di-shared/assets/img/car_management/genesis/tire_select_intro.png')}
                  fallback={
                    <div
                      className="tire_intro_img"
                      style={{height: 128}}
                    ></div>
                  }
                />
                <p
                  className="tire_intro_desc"
                  style={{whiteSpace: 'pre-wrap'}}
                >
                  {t('T30079')}
                </p>
              </div>
              <div className="box_outline_group">
                {recommendedTires?.map((tire) => {
                  const [frontTire, rearTire] = splitTireName(tire);
                  return (
                    <div
                      className="input_box input_box_outline"
                      key={tire}
                    >
                      <input
                        type="radio"
                        id={tire}
                        name="tireSelectGroup"
                        value={tire}
                        checked={selectedTire === tire}
                        className="input"
                        onChange={handleRadioChange}
                      />
                      <label
                        htmlFor={tire}
                        className="label"
                      >
                        {frontTire !== rearTire ? `${frontTire}(${t('T30015')}), ${rearTire}(${t('T30016')})` : tire}
                      </label>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="bottom_sheet_footer bottom_sheet_footer_complex">
              <button
                type="button"
                className="btn btn_large btn_outline"
                onClick={() => window.bottomSheetClose()}
              >
                {t('T20119')}
              </button>

              <button
                className="btn btn_large btn_filled"
                type={'submit'}
                disabled={!selectedTire}
              >
                {t('T10114')}
              </button>
            </div>
          </div>
        </PreventForm>
      </div>
    </BottomSheetWebViewWrapper>
  );
}

export default TireSelectBottomSheetKR;
